/**
 * WebSocket Hook and Context
 * 
 * Provides a SINGLE shared WebSocket connection for the entire app.
 * The connection is managed by WebSocketProvider and accessed via useWebSocket().
 * 
 * Usage:
 *   // In App.tsx or Layout - wrap with provider
 *   <WebSocketProvider>
 *     <YourApp />
 *   </WebSocketProvider>
 * 
 *   // In any component - use the hook
 *   const { isConnected, subscribe, addMessageHandler } = useWebSocket()
 *   
 *   // Subscribe to channel events
 *   subscribe([123])
 *   
 *   // Add a message handler (remember to clean up!)
 *   useEffect(() => {
 *     const unsubscribe = addMessageHandler((msg) => {
 *       console.log('Received:', msg)
 *     })
 *     return unsubscribe
 *   }, [addMessageHandler])
 *
 * Authentication (R22): the JWT is sent as the FIRST message
 * ({type: 'auth', payload: {token}}), never in the URL — uvicorn logs the full
 * handshake target, so `/ws?token=` wrote live JWTs into the server journal.
 * `isConnected` becomes true on the server's `auth.ok`, not on socket open, so
 * it means AUTHENTICATED; nothing else is sent before then.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react'

/**
 * WebSocket message envelope.
 */
export interface WSMessage {
  type: string
  channel_id?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: Record<string, any>
  timestamp: string
}

/**
 * WebSocket event types from the server.
 */
export type WSEventType =
  | 'chat.message'
  | 'chat.command'
  | 'song.now_playing'
  | 'song.queue_update'
  | 'module.status'
  | 'module.reloaded'
  | 'bot.status'
  | 'auth.ok'
  | 'pong'

/**
 * Message handler function type.
 */
type MessageHandler = (message: WSMessage) => void

/**
 * WebSocket context value.
 */
interface WebSocketContextValue {
  /** Whether we're currently connected */
  isConnected: boolean
  /** Subscribe to channel events */
  subscribe: (channelIds: number[]) => void
  /** Unsubscribe from channel events */
  unsubscribe: (channelIds: number[]) => void
  /** Add a message handler, returns unsubscribe function */
  addMessageHandler: (handler: MessageHandler) => () => void
  /** Manually connect */
  connect: () => void
  /** Manually disconnect */
  disconnect: () => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

/**
 * Build the WebSocket URL. Carries NO credential — the token goes in the
 * first message (R22).
 */
function buildWsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}/ws`
}

/**
 * WebSocketProvider - Manages the single WebSocket connection.
 * 
 * Wrap your app (or the authenticated portion) with this provider.
 */
export function WebSocketProvider({
  children,
  autoConnect = true,
  reconnectDelay = 3000,
}: {
  children: ReactNode
  autoConnect?: boolean
  reconnectDelay?: number
}) {
  const [isConnected, setIsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const pingIntervalRef = useRef<number | null>(null)
  const mountedRef = useRef(true)
  // True only between the server's auth.ok and the socket closing (R22).
  const authedRef = useRef(false)
  
  // Set of message handlers - using Set for easy add/remove
  const messageHandlersRef = useRef<Set<MessageHandler>>(new Set())
  
  /**
   * Send a message through the WebSocket.
   *
   * Only once authenticated: the server consumes the first frame as the auth
   * message, so anything sent ahead of auth.ok would be rejected with 4001.
   */
  const send = useCallback((message: Record<string, unknown>) => {
    if (authedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
    }
  }, [])
  
  /**
   * Subscribe to channel events.
   */
  const subscribe = useCallback((channelIds: number[]) => {
    send({
      type: 'subscribe',
      payload: { channel_ids: channelIds },
    })
  }, [send])
  
  /**
   * Unsubscribe from channel events.
   */
  const unsubscribe = useCallback((channelIds: number[]) => {
    send({
      type: 'unsubscribe',
      payload: { channel_ids: channelIds },
    })
  }, [send])
  
  /**
   * Add a message handler. Returns a function to remove it.
   */
  const addMessageHandler = useCallback((handler: MessageHandler): (() => void) => {
    messageHandlersRef.current.add(handler)
    return () => {
      messageHandlersRef.current.delete(handler)
    }
  }, [])
  
  /**
   * Disconnect from the WebSocket server.
   */
  const disconnect = useCallback(() => {
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    
    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect')
      wsRef.current = null
    }
    
    authedRef.current = false
    setIsConnected(false)
  }, [])
  
  /**
   * Connect to the WebSocket server.
   */
  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return
    }
    
    // Don't connect if unmounted
    if (!mountedRef.current) {
      return
    }
    
    const url = buildWsUrl()
    console.log('[WS] Connecting to:', url)
    
    const ws = new WebSocket(url)
    wsRef.current = ws
    
    ws.onopen = () => {
      if (!mountedRef.current) {
        ws.close()
        return
      }
      
      // Authenticate in the first message (R22). An empty token is still
      // sent: the server closes 4001 at once, rather than after its timeout.
      console.log('[WS] Open — authenticating')
      ws.send(JSON.stringify({
        type: 'auth',
        payload: { token: localStorage.getItem('pratbot_token') || '' },
      }))
    }
    
    ws.onclose = (event) => {
      console.log('[WS] Disconnected:', event.code, event.reason)
      
      if (!mountedRef.current) {
        return
      }
      
      authedRef.current = false
      setIsConnected(false)
      
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current)
        pingIntervalRef.current = null
      }
      
      // Schedule reconnect (unless it was a clean close, an auth rejection, or
      // the component unmounted). 4001 means the server refused our token —
      // retrying can only fail identically, and a logged-out tab would hammer
      // the socket forever.
      if (
        event.code !== 1000 &&
        event.code !== 1001 &&
        event.code !== 4001 &&
        mountedRef.current
      ) {
        console.log(`[WS] Will reconnect in ${reconnectDelay}ms...`)
        reconnectTimeoutRef.current = window.setTimeout(() => {
          if (mountedRef.current) {
            console.log('[WS] Attempting reconnect...')
            connect()
          }
        }, reconnectDelay)
      }
    }
    
    ws.onerror = (error) => {
      console.error('[WS] Error:', error)
    }
    
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WSMessage

        // auth.ok is the connection's own handshake, not an event: only now
        // is the socket "connected", and only now do pings start.
        if (message.type === 'auth.ok') {
          console.log('[WS] Connected')
          authedRef.current = true
          setIsConnected(true)
          if (pingIntervalRef.current) clearInterval(pingIntervalRef.current)
          pingIntervalRef.current = window.setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'ping' }))
            }
          }, 30000)
          return
        }

        // Dispatch to all registered handlers
        messageHandlersRef.current.forEach((handler) => {
          try {
            handler(message)
          } catch (err) {
            console.error('[WS] Handler error:', err)
          }
        })
      } catch (error) {
        console.error('[WS] Failed to parse message:', error)
      }
    }
  }, [reconnectDelay])
  
  /**
   * Auto-connect on mount, disconnect on unmount.
   */
  useEffect(() => {
    mountedRef.current = true
    
    if (autoConnect) {
      connect()
    }
    
    return () => {
      mountedRef.current = false
      disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])  // Intentionally empty - only run on mount/unmount
  
  const value: WebSocketContextValue = {
    isConnected,
    subscribe,
    unsubscribe,
    addMessageHandler,
    connect,
    disconnect,
  }
  
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

/**
 * useWebSocket - Hook to access the shared WebSocket connection.
 * 
 * Must be used within a WebSocketProvider.
 */
export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext)
  
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  
  return context
}

export default useWebSocket
